import {
  canAddToDynamicPortfolio,
  isHoldableByDynamicPortfolio,
  isWeightTotalComplete,
  sumWeights,
} from "@repo/shared";
import type { DynamicPortfolio } from "@repo/shared/types";
import { useMemo } from "react";
import {
  useAddDynamicPortfolioProduct,
  useCollectionRiskScores,
  useCreateDynamicPortfolio,
  useDeleteDynamicPortfolioProduct,
  usePlatforms,
  useProducts,
  useUpdateDynamicPortfolioProduct,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import {
  groupColumnsByScore,
  WeightsGrid,
  type WeightsGridColumn,
  type WeightsGridHandlers,
} from "../portfolio-weights";
import { T } from "./dynamic-portfolios-grid.i18n";

type DynamicPortfoliosGridProps = {
  portfolioCollectionId: string;
  dynamicPortfolios: DynamicPortfolio[];
};

export function DynamicPortfoliosGrid({
  portfolioCollectionId,
  dynamicPortfolios,
}: DynamicPortfoliosGridProps) {
  const localize = useLocalize();
  const t = useT(T);

  const { products } = useProducts(portfolioCollectionId);
  const { riskScores } = useCollectionRiskScores(portfolioCollectionId);
  const { platforms } = usePlatforms(portfolioCollectionId);
  const createDynamicPortfolio = useCreateDynamicPortfolio();
  const addProduct = useAddDynamicPortfolioProduct();
  const updateProduct = useUpdateDynamicPortfolioProduct();
  const deleteProduct = useDeleteDynamicPortfolioProduct();

  // One column per dynamic portfolio, the platform variants of a risk score
  // side by side.
  const columns = useMemo(
    () =>
      groupColumnsByScore(
        dynamicPortfolios,
        platforms,
        localize,
        (dp, { platformNames, hasSiblings }): WeightsGridColumn => {
          const weights = dp.products.map((row) => row.weight);
          const platformLabel = platformNames.length > 0 ? platformNames.join(", ") : null;
          return {
            id: dp.id,
            cells: Object.fromEntries(
              dp.products.map((row) => [row.productId, { rowId: row.id, weight: row.weight }]),
            ),
            total: sumWeights(weights),
            hasProducts: weights.length > 0,
            // An empty dynamic portfolio is incomplete too: it is not generated.
            isIncomplete: !isWeightTotalComplete(weights),
            inlineName: platformLabel ?? (hasSiblings ? t.noPlatform : null),
            tooltipTitle: t.columnTitle,
            tooltipDescription: platformLabel ?? t.noPlatform,
          };
        },
      ),
    [dynamicPortfolios, platforms, localize, t.columnTitle, t.noPlatform],
  );

  // Rows: what a dynamic portfolio may hold (active, not ordered in units),
  // plus anything one already holds so its weight stays visible.
  const rows = useMemo(() => {
    const held = new Set(dynamicPortfolios.flatMap((dp) => dp.products.map((p) => p.productId)));
    return products
      .filter((product) => held.has(product.id) || isHoldableByDynamicPortfolio(product))
      .map((product) => ({
        id: product.id,
        name: localize(product.nameI18n),
        isin: product.isin,
      }));
  }, [products, dynamicPortfolios, localize]);

  // A cell offers a new weight only where the server accepts the product: on
  // every platform of the column's dynamic portfolio. A risk score without one
  // gets a dynamic portfolio without a platform.
  const canAddCell = useMemo(() => {
    const productsById = new Map(products.map((product) => [product.id, product]));
    const platformIdsByPortfolio = new Map(dynamicPortfolios.map((dp) => [dp.id, dp.platformIds]));
    return (productId: string, columnId: string | null) => {
      const product = productsById.get(productId);
      const platformIds = columnId ? platformIdsByPortfolio.get(columnId) : [];
      return !!product && !!platformIds && canAddToDynamicPortfolio(product, platformIds);
    };
  }, [products, dynamicPortfolios]);

  const handlers: WeightsGridHandlers = {
    addCell: (dynamicPortfolioId, productId, weight) =>
      addProduct.mutateAsync({
        collectionId: portfolioCollectionId,
        dynamicPortfolioId,
        data: { productId, weight },
      }),
    updateCell: (dynamicPortfolioId, dynamicPortfolioProductId, weight) =>
      updateProduct.mutateAsync({
        collectionId: portfolioCollectionId,
        dynamicPortfolioId,
        dynamicPortfolioProductId,
        data: { weight },
      }),
    deleteCell: (dynamicPortfolioId, dynamicPortfolioProductId) =>
      deleteProduct.mutateAsync({
        collectionId: portfolioCollectionId,
        dynamicPortfolioId,
        dynamicPortfolioProductId,
      }),
    // A risk score without a dynamic portfolio gets one without a platform.
    createColumn: async (score) => {
      const created = await createDynamicPortfolio.mutateAsync({
        collectionId: portfolioCollectionId,
        data: { riskScore: score, platformIds: [] },
      });
      return created.id;
    },
    // Created with its first weight: a refused one creates nothing.
    createColumnWithCell: async (score, productId, weight) => {
      const created = await createDynamicPortfolio.mutateAsync({
        collectionId: portfolioCollectionId,
        data: { riskScore: score, platformIds: [], products: [{ productId, weight }] },
      });
      return created.id;
    },
  };

  return (
    <WeightsGrid
      title={t.gridTitle}
      description={t.gridDescription}
      scores={riskScores}
      products={rows}
      columns={columns}
      emptyColumnTitle={() => t.noDynamicPortfolio}
      weightStep="0.0001"
      canAddCell={canAddCell}
      notAddableHint={t.notAddable}
      handlers={handlers}
    />
  );
}
